import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Play, Pause } from "lucide-react";
import { HERO_HOME_URL, HOMEPAGE_INTRO_VIDEOS } from "@/lib/assets";

const HERO_BG = HERO_HOME_URL;

function IntroVideoCard({ v, i }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
      className="group"
      data-testid={`intro-video-card-${i}`}
    >
      <div className="glass rounded-2xl p-3 hover:-translate-y-1 transition-transform duration-300">
        <div className="relative rounded-xl overflow-hidden aspect-video bg-navy-900">
          <video
            ref={ref}
            src={v.src}
            preload="metadata"
            playsInline
            controls={playing}
            onEnded={() => setPlaying(false)}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`intro-video-${i}`}
          />
          {!playing && (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 to-navy-900/10 pointer-events-none" />
              <button
                onClick={toggle}
                data-testid={`intro-video-play-${i}`}
                aria-label={`Play ${v.title}`}
                className="absolute inset-0 flex items-center justify-center group/vid"
              >
                <span className="relative h-16 w-16 rounded-full bg-gold-500 text-navy-900 flex items-center justify-center shadow-2xl transition-all group-hover/vid:scale-110">
                  <Play size={22} className="ml-1 fill-navy-900" />
                  <span className="absolute inset-0 rounded-full bg-gold-500/40 animate-ping" />
                </span>
              </button>
              <div className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-800">
                {v.tag}
              </div>
            </>
          )}
          {playing && (
            <button
              onClick={toggle}
              data-testid={`intro-video-pause-${i}`}
              aria-label="Pause"
              className="absolute top-3 right-3 h-9 w-9 rounded-full glass-dark flex items-center justify-center text-white z-10"
            >
              <Pause size={14} />
            </button>
          )}
        </div>
        <div className="px-2 pt-3 pb-1">
          <div className="font-heading text-base text-navy-900 font-semibold leading-tight">{v.title}</div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center pt-32 pb-24 overflow-hidden isolate"
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="Luxury estate at sunset" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
        {/* warm Orange County sunset glow */}
        <div className="absolute top-0 right-0 h-[34rem] w-[34rem] rounded-full bg-gold-400/25 blur-3xl" />
        <div className="absolute top-1/4 -right-10 h-72 w-72 rounded-full bg-[#ff8a5c]/15 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 mb-6" data-testid="hero-tagline">
            <Sparkles size={14} className="text-gold-600" />
            <span className="text-xs uppercase tracking-[0.22em] text-navy-700">Compassionate Probate Real Estate</span>
          </div>
          <h1 className="font-heading text-navy-900 leading-[1.03] tracking-tight">
            <span className="block text-5xl sm:text-6xl lg:text-7xl">
              Give <em className="italic font-medium text-gold-600">Love</em> and
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl">
              <em className="italic font-medium text-gold-600">Compassion</em>.
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl mt-1">It&apos;s Free.</span>
          </h1>
          <p className="mt-7 max-w-xl text-lg text-navy-700/80 leading-relaxed">
            Probate Solution OC walks families through every step of probate and estate transitions — with patience, integrity, and a luxury-grade real estate experience built around your peace of mind.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#services" data-testid="hero-find-out-more" className="btn-primary group">
              Find Out More
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#free-package" data-testid="hero-get-free-package" className="btn-gold group">
              Get Your FREE Package Today
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </motion.div>

        {/* Three intro videos row */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="intro-videos-row">
          {HOMEPAGE_INTRO_VIDEOS.map((v, i) => (
            <IntroVideoCard key={v.title} v={v} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
